import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface SearchFieldProps {
  /** Accessible name of the field; also the placeholder unless one is given. */
  label: string;
  /** The committed value (for example the URL's `q`). */
  value: string;
  /** Called with the typed value once typing pauses, and at once on clear. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Debounce in ms. Defaults to 300. */
  delay?: number;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

/** A labelled search box, debounced, with a search icon and a clear button. */
export const SearchField = ({
  label,
  value,
  onChange,
  placeholder,
  delay = 300,
  fullWidth = false,
  sx,
}: SearchFieldProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  const onChangeRef = useRef(onChange);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Follow a value changed from outside (back button, a cleared filter), not our own echo.
  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(() => {
    if (draft === committed.current) return undefined;
    const timer = setTimeout(() => {
      committed.current = draft;
      onChangeRef.current(draft);
    }, delay);
    return () => clearTimeout(timer);
  }, [draft, delay]);

  const clear = () => {
    setDraft("");
    committed.current = "";
    onChangeRef.current("");
    inputRef.current?.focus();
  };

  return (
    <TextField
      type="search"
      size="small"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      placeholder={placeholder ?? label}
      fullWidth={fullWidth}
      inputRef={inputRef}
      inputProps={{ "aria-label": label }}
      sx={[
        {
          "& input::-webkit-search-cancel-button": { display: "none" },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" aria-hidden="true" />
          </InputAdornment>
        ),
        endAdornment: draft ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              onClick={clear}
              aria-label={t("common.clearSearch")}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

export default SearchField;
